"use client";
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function DualCTAPanel() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="relative overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 min-h-[480px]">
        {/* Left: For Businesses */}
        <div className="relative overflow-hidden group cursor-pointer min-h-[480px]" onClick={() => handleScroll('contact')}>
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_199cb0705-1772906007803.png"
            alt="Business executive in a suit reviewing workforce documents in office"
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 50vw" />
          
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ background: 'linear-gradient(135deg, rgba(26,86,176,0.85) 0%, rgba(2,13,31,0.7) 100%)' }} />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 reveal-hidden stagger-1">
            <span
              className="eyebrow mb-4 text-white opacity-80">
              
              FOR BUSINESSES
            </span>
            <h3
              className="text-3xl lg:text-4xl font-extrabold text-white mb-4"
              style={{ fontFamily: 'Syne, sans-serif' }}>
              
              Looking to Hire?
            </h3>
            <p
              className="text-[rgba(240,244,255,0.8)] text-base mb-8 max-w-xs leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
              
              Tell us your workforce requirements and we'll have qualified candidates ready in 72 hours.
            </p>
            <button
              className="btn-gradient px-8 py-3.5 rounded-full font-semibold text-sm"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              
              <span>Request a Proposal →</span>
            </button>
          </div>
        </div>

        {/* Right: For Job Seekers */}
        <div className="relative overflow-hidden group cursor-pointer min-h-[480px]" onClick={() => handleScroll('contact')}>
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1b4d82006-1772906006236.png"
            alt="Skilled worker in safety helmet ready for work at industrial site"
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 50vw" />
          
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ background: 'linear-gradient(135deg, rgba(0,194,255,0.7) 0%, rgba(2,13,31,0.75) 100%)' }} />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 reveal-hidden stagger-2">
            <span
              className="eyebrow mb-4 text-white opacity-80">
              
              FOR JOB SEEKERS
            </span>
            <h3
              className="text-3xl lg:text-4xl font-extrabold text-white mb-4"
              style={{ fontFamily: 'Syne, sans-serif' }}>
              
              Looking for Work?
            </h3>
            <p
              className="text-[rgba(240,244,255,0.8)] text-base mb-8 max-w-xs leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
              
              Join thousands of professionals working across Qatar's most prestigious projects and companies.
            </p>
            <button
              className="btn-outline px-8 py-3.5 rounded-full font-semibold text-sm border-white/40 text-white hover:border-white hover:bg-white/10"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              
              Find Jobs →
            </button>
            <a
              href="https://whatsapp.com/channel/0029Va8Cw5jLtOj752Vxvz1x"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-4 flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm text-white transition-all duration-300"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: 'rgba(37,211,102,0.85)', border: '1.5px solid rgba(37,211,102,0.5)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(37,211,102,1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(37,211,102,0.85)')}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Join WhatsApp for Job Updates
            </a>
          </div>
        </div>
      </div>
    </section>);

}